import React, { useState, useMemo, useCallback } from 'react';
import { Settings, Target } from 'lucide-react';
import { useSelectedMetrics, useComponentMetrics } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

/**
 * Composant table des types de campagnes Microsoft Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const CampaignTypesTable = React.memo(({
  campaignsData = [],
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedAccount = null,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'msads_campaign_types'
}) => {
  // États locaux pour cette table
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showCampaignTypes, setShowCampaignTypes] = useState(true);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques sélectionnées
  const selectedMetrics = useSelectedMetrics();

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('msads_campaign_types', selectedAccount?.id || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Mapping des types de campagnes Microsoft Ads vers des noms français
  const getChannelTypeName = useCallback((channelType) => {
    const channelTypeMapping = {
      'Search': 'Recherche',
      'Search & content': 'Recherche',
      'Shopping': 'Shopping', 
      'Display': 'Display',
      'Video': 'Vidéo',
      'Discovery': 'Découverte',
      'Performance Max': 'Performance Max',
      'App': 'Application',
      'Local': 'Local',
      'Smart': 'Intelligente',
      'Hotel': 'Hôtel',
      'DynamicSearchAds': 'Annonces de recherche dynamique',
      'Audience': 'Display',
      'MaxConversions': 'Conversions maximales',
      // Mappings avec préfixe "Type " pour Microsoft Ads
      'Type Search & content': 'Recherche',
      'Type Shopping': 'Shopping',
      'Type Performance max': 'Performance Max',
      'Type Audience': 'Display'
    };
    
    return channelTypeMapping[channelType] || channelType || 'Non défini';
  }, []);

  // Calcul des données agrégées par type de campagne (memoized)
  const campaignTypesData = useMemo(() => {
    if (!campaignsData || campaignsData.length === 0) return [];

    // Grouper les campagnes par type et calculer les totaux
    const campaignTypeGroups = {};
    campaignsData.forEach(campaign => {
      // Utiliser le type de campagne depuis l'API Microsoft Ads
      const originalType = campaign.advertisingChannelType || campaign.type;
      const campaignType = getChannelTypeName(originalType);
      
      if (!campaignTypeGroups[campaignType]) {
        campaignTypeGroups[campaignType] = {
          type: campaignType,
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0,
          campaignCount: 0
        };
      }
      
      campaignTypeGroups[campaignType].impressions += campaign.impressions || 0;
      campaignTypeGroups[campaignType].clicks += campaign.clicks || 0;
      campaignTypeGroups[campaignType].cost += campaign.cost || 0;
      campaignTypeGroups[campaignType].conversions += campaign.conversions || 0;
      campaignTypeGroups[campaignType].conversionsValue += campaign.conversionsValue || 0;
      campaignTypeGroups[campaignType].campaignCount += 1;
    });

    // Convertir en array et calculer les métriques dérivées
    return Object.values(campaignTypeGroups).map(group => ({
      ...group,
      ctr: group.impressions > 0 ? (group.clicks / group.impressions * 100) : 0,
      cpc: group.clicks > 0 ? (group.cost / group.clicks) : 0,
      conversionRate: group.clicks > 0 ? (group.conversions / group.clicks * 100) : 0,
      cpa: group.conversions > 0 ? (group.cost / group.conversions) : 0,
      cos: group.conversionsValue > 0 ? (group.cost / group.conversionsValue * 100) : 0
    }));
  }, [campaignsData, getChannelTypeName]);

  // Fonction de tri memoized
  const sortedCampaignTypes = useMemo(() => {
    if (!campaignTypesData.length) return [];

    const sorted = [...campaignTypesData];
    sorted.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [campaignTypesData, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!campaignTypesData.length) return {};

    const baseTotals = campaignTypesData.reduce((acc, type) => ({
      impressions: acc.impressions + (type.impressions || 0),
      clicks: acc.clicks + (type.clicks || 0),
      cost: acc.cost + (type.cost || 0),
      conversions: acc.conversions + (type.conversions || 0),
      conversionsValue: acc.conversionsValue + (type.conversionsValue || 0),
      campaignCount: acc.campaignCount + (type.campaignCount || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, campaignCount: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [campaignTypesData]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Ne pas rendre si pas de données
  if (!campaignTypesData.length) return null;

  // Vérification de visibilité pour les clients
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600' }}><Target size={16} />&nbsp;Types de campagnes</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Types de campagnes Microsoft Ads"
            />
          ) : (
            <button
              onClick={() => setShowCampaignTypes(!showCampaignTypes)}
              className="section-toggle-btn"
              title={showCampaignTypes ? "Masquer la section" : "Afficher la section"}
            >
              {showCampaignTypes ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && showCampaignTypes && (
        <div className="campaign-types-table-wrapper">
          <table className="campaign-types-table" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th onClick={() => handleSort('type')} className="sortable">
                  Type {sortConfig.key === 'type' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('campaignCount')} className="sortable numeric">
                  Campagnes {sortConfig.key === 'campaignCount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                {isComponentMetricActive('impressions') && (
                  <th onClick={() => handleSort('impressions')} className="sortable numeric">
                    Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('ctr') && (
                  <th onClick={() => handleSort('ctr')} className="sortable numeric">
                    CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('clics') && (
                  <th onClick={() => handleSort('clicks')} className="sortable numeric">
                    Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('cpc') && (
                  <th onClick={() => handleSort('cpc')} className="sortable numeric">
                    CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('cout') && (
                  <th onClick={() => handleSort('cost')} className="sortable numeric">
                    Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('taux_conv') && (
                  <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                    Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('conv') && (
                  <th onClick={() => handleSort('conversions')} className="sortable numeric">
                    Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('cpa') && (
                  <th onClick={() => handleSort('cpa')} className="sortable numeric">
                    CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('valeur_conv') && (
                  <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                    Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
                {isComponentMetricActive('cos') && (
                  <th onClick={() => handleSort('cos')} className="sortable numeric">
                    COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                )}
              </tr>
            </thead>
            
            <tbody>
              {sortedCampaignTypes.map((campaignType, index) => (
                <tr key={index}>
                  <td className="campaign-type-name">{campaignType.type}</td>
                  <td className="numeric">{formatNumber(campaignType.campaignCount)}</td>
                  {isComponentMetricActive('impressions') && (
                    <td className="numeric">{formatNumber(campaignType.impressions)}</td>
                  )}
                  {isComponentMetricActive('ctr') && (
                    <td className="numeric">{formatNumber(campaignType.ctr, 2)}%</td>
                  )}
                  {isComponentMetricActive('clics') && (
                    <td className="numeric">{formatNumber(campaignType.clicks)}</td>
                  )}
                  {isComponentMetricActive('cpc') && (
                    <td className="numeric">€{formatNumber(campaignType.cpc, 2)}</td>
                  )}
                  {isComponentMetricActive('cout') && (
                    <td className="numeric">€{formatNumber(campaignType.cost, 2)}</td>
                  )}
                  {isComponentMetricActive('taux_conv') && (
                    <td className="numeric">{formatNumber(campaignType.conversionRate, 2)}%</td>
                  )}
                  {isComponentMetricActive('conv') && (
                    <td className="numeric">{formatNumber(campaignType.conversions)}</td>
                  )}
                  {isComponentMetricActive('cpa') && (
                    <td className="numeric">{campaignType.cpa >= 0 ? `€${formatNumber(campaignType.cpa, 2)}` : '-'}</td>
                  )}
                  {isComponentMetricActive('valeur_conv') && (
                    <td className="numeric">€{formatNumber(campaignType.conversionsValue, 2)}</td>
                  )}
                  {isComponentMetricActive('cos') && (
                    <td className="numeric">{formatNumber(campaignType.cos, 1)}%</td>
                  )}
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="totals-row">
                <td><strong>TOTAL</strong></td>
                <td className="numeric"><strong>{formatNumber(totals.campaignCount)}</strong></td>
                {isComponentMetricActive('impressions') && (
                  <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                )}
                {isComponentMetricActive('ctr') && (
                  <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                )}
                {isComponentMetricActive('clics') && (
                  <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                )}
                {isComponentMetricActive('cpc') && (
                  <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                )}
                {isComponentMetricActive('cout') && (
                  <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                )}
                {isComponentMetricActive('taux_conv') && (
                  <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                )}
                {isComponentMetricActive('conv') && (
                  <td className="numeric"><strong>{formatNumber(totals.conversions)}</strong></td>
                )}
                {isComponentMetricActive('cpa') && (
                  <td className="numeric"><strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong></td>
                )}
                {isComponentMetricActive('valeur_conv') && (
                  <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                )}
                {isComponentMetricActive('cos') && (
                  <td className="numeric"><strong>{formatNumber(totals.cos, 1)}%</strong></td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="msads_campaign_types"
          accountId={selectedAccount?.id || 'global'}
          title="Types de campagnes Microsoft Ads"
          hideApplyToAll={true}
        />
      )}
    </div>
  );
});

CampaignTypesTable.displayName = 'CampaignTypesTable';

export default CampaignTypesTable;